import { createStyles, Stack, Tooltip, useMantineTheme } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';

import { PaymentType } from '~/common/enums/payments.enum';
import { Text } from '~/domains/design-system/Text';
import { useIsCanada } from '~/domains/common/hooks/useIsCanada';
import dt from '~/testing';
import type { BankAccount as BankAccountType } from '~/services/wallet/interfaces/wallets.interface';
import { IconAeropayWithText } from '~/domains/payments/icons/BankIcons';
import { Tag } from '~/domains/design-system/Tag';

import {
  DEFAULT_ICON_SLOT,
  PAYMENT_TYPE_ICON_SLOT,
  SELF_FRAMED_PAYMENT_TYPES,
} from '../../constants';
import PaymentTypeIcon from '../../icons/PaymentTypeIcon';
import type { PaymentTypeSettings } from '../hooks/useWithdrawalPaymentMethods';
import { useDeleteAeropayAccount } from '../../hooks/useDeleteAeropay';
import { BankAccount } from '../../deposits/components/BankAccount';

const useStyles = createStyles((theme, { isBankAccount }: { isBankAccount?: boolean }) => ({
  container: {
    '&.isFullWidthRadio': {
      gridColumn: '1 / span 2',
    },
  },
  label: {
    position: 'relative' as const,
    width: '100%',
    border: `1px solid ${theme.colors.gray2[1]}`,
    borderRadius: theme.radius.md,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    height: isBankAccount ? 'auto' : 96,
    transition: 'border-color 150ms ease-in-out',

    ...(!isBankAccount && {
      '&[data-payment-type="AEROPAY"]': {
        padding: /* eslint-disable-line local-rules/use-theme-tokens */ '24px',
        height: 'auto',
      },
    }),

    '&[data-disabled]': {
      cursor: 'not-allowed',
      opacity: 0.5,
    },
  },
  input: {
    position: 'absolute' as const,
    width: 1,
    height: 1,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap' as const,

    '&:checked ~ label': {
      background: theme.white,
      borderColor: theme.colors.gray[7],
    },

    '&:focus-visible ~ label': {
      outline: `2px solid ${theme.colors.splash[8]}`,
      outlineOffset: 2,
    },
  },
  newBadge: {
    position: 'absolute',
    top: 7,
    right: 7,
    zIndex: 1,
    background: theme.colors.blue[1],
    borderRadius: /* eslint-disable-line local-rules/use-theme-tokens */ 50,
    padding: /* eslint-disable-line local-rules/use-theme-tokens */ '2px 6px',
    fontSize: 8,
    fontWeight: 600,
    lineHeight: '12px',
    color: theme.colors.gray[7],
  },
  visuallyHidden: {
    position: 'absolute' as const,
    width: 1,
    height: 1,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap' as const,
  },
}));

type PaymentTypeRadioProps = {
  type: PaymentType;
  value: string;
  settings: PaymentTypeSettings;
  isActive: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  superscriptIndex: number;
  // in withdrawal form, we have a full width radio for recommended VIPPreferred and Skrill
  isWithdrawal?: boolean;
  bankAccount?: BankAccountType;
  isDeleteBankAccountVisible?: boolean;
  description?: string;
  recommended?: boolean;
  isNew?: boolean;
};

export function PaymentTypeRadio({
  type,
  value,
  settings,
  isActive,
  onChange,
  superscriptIndex,
  isWithdrawal = true,
  bankAccount,
  isDeleteBankAccountVisible = false,
  description,
  recommended = false,
  isNew = false,
}: PaymentTypeRadioProps) {
  const { t } = useTranslation('payments');
  const isCanada = useIsCanada();
  const { classes, cx, theme } = useStyles({ isBankAccount: !!bankAccount });

  const { mutate: deleteAeropayAccount, isLoading: isDeletingAeropayAccount } =
    useDeleteAeropayAccount();

  const iconSlot = PAYMENT_TYPE_ICON_SLOT[type] ?? DEFAULT_ICON_SLOT;

  // Only debit cards are withdrawable; usePaymentMethods filters out credit cards.
  const paymentName =
    type === PaymentType.CARD ? t('paymentTypes.DEBIT_CARD') : t(`paymentTypes.${type}`);

  const isFullWidthRadio =
    isWithdrawal &&
    (type === PaymentType.VIPPREFERRED || (type === PaymentType.SKRILL && isCanada));

  const disabledReasonText =
    settings.disabledReason ??
    t('deposits.tooltip.disabledPaymentMethod', { paymentMethod: paymentName });
  const reasonId = `${type}-${value}-disabled-reason`;

  const CustomContent = (
    <div className={cx(classes.container, { isFullWidthRadio })}>
      {recommended && (
        <div style={{ position: 'relative', zIndex: 2 }}>
          <Tag marginBottom={-theme.other.spacing._12}>
            {t('deposits.recommendedDepositMethods')}
          </Tag>
        </div>
      )}
      <input
        type="radio"
        name="paymentType"
        id={`${type}-${value}`}
        value={value}
        checked={isActive}
        onChange={onChange}
        disabled={settings.isDisabled}
        aria-describedby={settings.isDisabled ? reasonId : undefined}
        aria-label={t(`paymentTypes.${type}`)}
        className={classes.input}
      />

      <label
        className={classes.label}
        htmlFor={`${type}-${value}`}
        data-disabled={settings.isDisabled ? true : undefined}
        data-payment-type={!bankAccount ? type : undefined}
      >
        {isNew && <span className={classes.newBadge}>{t('newBadge')}</span>}
        {bankAccount ? (
          <BankAccount
            bankAccount={bankAccount}
            isDeleteBankAccountVisible={isDeleteBankAccountVisible}
            isDeletingAeropayAccount={isDeletingAeropayAccount}
            deleteAeropayAccount={deleteAeropayAccount}
          />
        ) : (
          <Stack
            data-test-id={dt.payments.withdrawal.components.paymentType}
            spacing={theme.other.spacing._8}
          >
            <Stack
              w={iconSlot.width}
              h={24}
              align="center"
              justify="center"
              sx={
                SELF_FRAMED_PAYMENT_TYPES.includes(type)
                  ? { alignSelf: 'center' }
                  : {
                      alignSelf: 'center',
                      borderRadius: theme.radius.sm,
                      border: `1px solid ${theme.colors.gray[2]}`,
                    }
              }
            >
              <PaymentTypeIcon
                type={type}
                color={settings.isDisabled ? theme.colors.gray[6] : undefined}
                size={iconSlot.size}
              />
            </Stack>
            <Text
              variant="subhead-small"
              color={settings.isDisabled ? 'gray.6' : undefined}
              ta="center"
            >
              {paymentName}
              {type === PaymentType.AEROPAY && (
                <Text component="span" inline variant="subhead-small">
                  {' via '}
                  <IconAeropayWithText />
                </Text>
              )}
              {!settings.isFree ? (
                <Text
                  variant="body-small"
                  component="sup"
                  c="gray.6"
                  sx={{ marginLeft: theme.other.spacing._2 }}
                >
                  {superscriptIndex}
                </Text>
              ) : null}

              {description && (
                <Text variant="body-small" color="gray.6" ta="center" component="div">
                  {description}
                </Text>
              )}
            </Text>
          </Stack>
        )}
      </label>
    </div>
  );

  if (settings.isDisabled) {
    return (
      <Tooltip
        key={type}
        label={disabledReasonText}
        multiline
        events={{ hover: true, focus: true, touch: true }}
        zIndex={99}
      >
        <div
          // Div wrapper is required for proper event propagation. Without it, hover/focus events might not bubble up correctly to the Tooltip.
          style={{ width: '100%' }}
        >
          {CustomContent}
          <span className={classes.visuallyHidden} id={reasonId} aria-hidden="true">
            {disabledReasonText}
          </span>
        </div>
      </Tooltip>
    );
  }

  return CustomContent;
}
